import { create } from "zustand";
import { invoke } from "@tauri-apps/api/core";
import { listen } from "@tauri-apps/api/event";
import { isTauri } from "@/lib/is-tauri";
import {
  bindingSignature,
  normalizeShortcutBinding,
  getDefaultBinding,
  validateShortcutOverrides,
  shortcutPlatform,
  SHORTCUT_REGISTRY,
  type ShortcutBinding,
  type ShortcutId,
} from "@/lib/shortcut-registry";
import { translate } from "@/i18n";
import { libShortcutsMessages } from "@/i18n/messages/lib-shortcuts";

type Overrides = Partial<Record<ShortcutId, ShortcutBinding>>;
/**
 * A disabled shortcut matches nothing and conflicts with nothing. `needsKey`
 * lists the disabled ones that are off only because another binding took
 * their keys (native decides it at load); they turn on once the keys are free.
 */
type Saved = {
  overrides: Overrides;
  disabled: ShortcutId[];
  needsKey: ShortcutId[];
};
type ShortcutState = Saved & {
  loading: boolean;
  error: string | null;
  hydrate: () => Promise<void>;
  save: (id: ShortcutId, binding: ShortcutBinding) => Promise<void>;
  reset: (id: ShortcutId) => Promise<void>;
  resetAll: () => Promise<void>;
  setDisabled: (id: ShortcutId, disabled: boolean) => Promise<void>;
};
const STORAGE_KEY = "denker.shortcuts";
let hydration: Promise<void> | null = null;
let recording = false;
let changes = 0;
let statusChanges = 0;
let statusListener: Promise<() => void> | null = null;
let nativeListener: Promise<() => void> | null = null;
let suspension: Promise<unknown> = Promise.resolve();

export function isShortcutRecording(): boolean {
  return recording;
}
export function isShortcutDisabled(id: ShortcutId): boolean {
  return useShortcutStore.getState().disabled.includes(id);
}
/** On by the user's choice, but its keys are taken: it needs keys of its own. */
export function shortcutNeedsKey(state: Saved, id: ShortcutId): boolean {
  return state.disabled.includes(id) && state.needsKey.includes(id);
}
export async function suspendShortcutDispatch(active: boolean): Promise<void> {
  recording = active;
  if (isTauri()) {
    try {
      const operation = suspension
        .catch(() => {})
        .then(() => invoke("set_shortcut_recording", { recording: active }));
      suspension = operation;
      await operation;
    } catch (error) {
      recording = false;
      throw error;
    }
  }
}
async function refreshNativeStatus(): Promise<void> {
  const beforeStatus = statusChanges;
  const error = await invoke<string | null>("get_shortcut_status");
  if (statusChanges === beforeStatus) useShortcutStore.setState({ error });
}
function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}
function isKnownId(id: unknown): id is ShortcutId {
  return SHORTCUT_REGISTRY.some((item) => item.id === id);
}
/**
 * Native's settings in the store's shape. Setting anything else would replace
 * the whole store, actions included, so missing or malformed parts fall back
 * to defaults and unknown ids are dropped.
 */
function nativeSaved(payload: unknown): Saved {
  const value = isRecord(payload) ? payload : {};
  const ids = (list: unknown) =>
    Array.isArray(list) ? list.filter(isKnownId) : [];
  const overrides = Object.fromEntries(
    Object.entries(isRecord(value.overrides) ? value.overrides : {}).filter(
      ([id, binding]) =>
        isKnownId(id) &&
        isRecord(binding) &&
        Array.isArray(binding.keys) &&
        typeof binding.gesture === "string",
    ),
  ) as Overrides;
  return {
    overrides,
    disabled: ids(value.disabled),
    needsKey: ids(value.needsKey),
  };
}
/** Stored like the native file: overrides at the top level, then `disabled`. */
function localSaved(): Saved {
  const raw = window.localStorage.getItem(
    `${STORAGE_KEY}.${shortcutPlatform()}`,
  );
  const parsed: unknown = raw ? JSON.parse(raw) : {};
  if (!isRecord(parsed))
    throw new Error(translate(libShortcutsMessages, "savedUnreadable"));
  const { disabled = [], ...overrides } = parsed;
  validateShortcutOverrides(overrides, disabled);
  return { overrides, disabled: disabled as ShortcutId[], needsKey: [] };
}
/** Native applies the change itself; the browser applies `local` and stores it. */
async function apply(
  command: string,
  args: Record<string, unknown>,
  local: (saved: Saved) => Saved,
): Promise<void> {
  useShortcutStore.setState({ loading: true, error: null });
  try {
    let saved: Saved;
    const beforeChanges = changes;
    if (isTauri()) {
      saved = nativeSaved(await invoke<unknown>(command, args));
    } else {
      const { overrides, disabled, needsKey } = useShortcutStore.getState();
      saved = local({ overrides: { ...overrides }, disabled, needsKey });
      validateShortcutOverrides(saved.overrides, saved.disabled);
      window.localStorage.setItem(
        `${STORAGE_KEY}.${shortcutPlatform()}`,
        JSON.stringify({
          ...saved.overrides,
          ...(saved.disabled.length ? { disabled: saved.disabled } : {}),
        }),
      );
    }
    // A later save in another webview can be delivered before this reply.
    if (changes === beforeChanges) useShortcutStore.setState(saved);
    useShortcutStore.setState({ loading: false });
  } catch (error) {
    useShortcutStore.setState({
      loading: false,
      error: String(error instanceof Error ? error.message : error),
    });
    throw error;
  }
}
function update(id?: ShortcutId, binding?: ShortcutBinding): Promise<void> {
  if (binding) binding = normalizeShortcutBinding(binding);
  return apply(
    id ? "set_shortcut_binding" : "reset_shortcut_bindings",
    id ? { id, binding: binding ?? null } : {},
    ({ overrides, disabled, needsKey }) => {
      if (!id) overrides = {};
      else if (
        binding &&
        bindingSignature(binding) !== bindingSignature(getDefaultBinding(id))
      )
        overrides[id] = binding;
      else delete overrides[id];
      return { overrides, disabled, needsKey };
    },
  );
}
export const useShortcutStore = create<ShortcutState>(() => ({
  overrides: {},
  disabled: [],
  needsKey: [],
  loading: false,
  error: null,
  hydrate: () => {
    if (hydration)
      return hydration.then(async () => {
        if (isTauri()) {
          try {
            await refreshNativeStatus();
          } catch (error) {
            useShortcutStore.setState({ error: String(error) });
          }
        }
      });
    useShortcutStore.setState({ loading: true, error: null });
    hydration = (async () => {
      try {
        if (isTauri()) {
          // Subscribe before fetching, so a save in another webview is not lost.
          if (!nativeListener) {
            nativeListener = listen<unknown>(
              "shortcut-bindings-changed",
              ({ payload }) => {
                changes += 1;
                useShortcutStore.setState(nativeSaved(payload));
              },
            ).catch((error: unknown) => {
              nativeListener = null;
              throw error;
            });
          }
          if (!statusListener) {
            statusListener = listen<string | null>(
              "shortcut-registration-status",
              ({ payload }) => {
                statusChanges += 1;
                useShortcutStore.setState({ error: payload });
              },
            ).catch((error: unknown) => {
              statusListener = null;
              throw error;
            });
          }
          await Promise.all([nativeListener, statusListener]);
          const beforeChanges = changes;
          const saved = nativeSaved(
            await invoke<unknown>("get_saved_shortcuts"),
          );
          if (changes === beforeChanges) useShortcutStore.setState(saved);
          await refreshNativeStatus();
        } else {
          useShortcutStore.setState(localSaved());
          window.addEventListener("storage", (event) => {
            if (event.key === `${STORAGE_KEY}.${shortcutPlatform()}`) {
              try {
                useShortcutStore.setState(localSaved());
              } catch {
                useShortcutStore.setState({
                  error: translate(libShortcutsMessages, "savedUnreadable"),
                });
              }
            }
          });
        }
        useShortcutStore.setState({ loading: false });
      } catch (error) {
        useShortcutStore.setState({ loading: false, error: String(error) });
        hydration = null;
      }
    })();
    return hydration;
  },
  save: (id, binding) => update(id, binding),
  reset: (id) => update(id),
  resetAll: () => update(),
  setDisabled: (id, disabled) =>
    apply("set_shortcut_disabled", { id, disabled }, (saved) => ({
      ...saved,
      disabled: disabled
        ? [...new Set([...saved.disabled, id])]
        : saved.disabled.filter((other) => other !== id),
    })),
}));
